/* ==========================================================================
   Christopher Kyle — Gallery (1b)
   Vanilla JS. No framework, no jQuery. Everything is a custom element or a
   small delegated handler, so sections can be added/removed in the theme
   editor without re-initialising the whole page.
   ========================================================================== */

(function () {
  'use strict';

  var routes = window.routes || {};
  var strings = window.themeStrings || {};

  /* ---------------------------------------------------------------- utils */

  function announce(message) {
    var region = document.getElementById('LiveRegion');
    if (region) region.textContent = message;
  }

  function formatMoney(cents) {
    // Shopify returns cents. We render with the shop's own format where we
    // can, but the drawer needs a client-side fallback.
    var amount = (cents / 100).toFixed(2);
    return '$' + amount.replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      if (!response.ok) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          var error = new Error(body.description || body.message || strings.cartError || 'Request failed');
          error.body = body;
          throw error;
        });
      }
      return response.json();
    });
  }

  var focusTrapStack = [];

  function trapFocus(container, opener) {
    var selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var focusables = Array.prototype.filter.call(
        container.querySelectorAll(selector),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    focusTrapStack.push({ container: container, handler: onKeydown, opener: opener });

    var target = container.querySelector(selector);
    if (target) target.focus();
  }

  function releaseFocus() {
    var entry = focusTrapStack.pop();
    if (!entry) return;
    entry.container.removeEventListener('keydown', entry.handler);
    if (entry.opener && typeof entry.opener.focus === 'function') entry.opener.focus();
  }

  function lockScroll(locked) {
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  /* ------------------------------------------------------------- overlays */

  /**
   * Shared open/close behaviour for the nav drawer, cart drawer and search
   * modal. Each is a plain element with `.is-open`, so CSS owns the motion.
   */
  var Overlay = {
    open: function (el, opener) {
      if (!el) return;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      trapFocus(el, opener);
    },
    close: function (el) {
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      releaseFocus();
    },
    closeAll: function () {
      document.querySelectorAll('.is-open[data-overlay]').forEach(function (el) {
        Overlay.close(el);
      });
    }
  };

  document.addEventListener('click', function (event) {
    var openTrigger = event.target.closest('[data-overlay-open]');
    if (openTrigger) {
      var targetSel = openTrigger.getAttribute('data-overlay-open');
      var target = document.querySelector(targetSel);
      if (target) {
        event.preventDefault();
        Overlay.open(target, openTrigger);
      }
      return;
    }

    if (event.target.closest('[data-overlay-close]')) {
      var overlay = event.target.closest('[data-overlay]');
      Overlay.close(overlay);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') Overlay.closeAll();
  });

  /* ------------------------------------------------------------ accordion */

  /**
   * Height-animated accordion. Uses explicit pixel heights so the transition
   * works, then clears to `auto` so nested content can reflow.
   */
  function initAccordion(root) {
    root.querySelectorAll('.accordion__trigger').forEach(function (trigger) {
      if (trigger.dataset.bound === 'true') return;
      trigger.dataset.bound = 'true';

      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      if (trigger.getAttribute('aria-expanded') === 'true') {
        panel.style.height = 'auto';
      }

      trigger.addEventListener('click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));

        if (expanded) {
          panel.style.height = panel.scrollHeight + 'px';
          requestAnimationFrame(function () { panel.style.height = '0px'; });
        } else {
          panel.style.height = panel.scrollHeight + 'px';
          panel.addEventListener('transitionend', function done(e) {
            if (e.propertyName !== 'height') return;
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', done);
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------- quantity */

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quantity-change]');
    if (!button) return;

    var wrapper = button.closest('.quantity');
    var input = wrapper && wrapper.querySelector('input');
    if (!input) return;

    var delta = parseInt(button.getAttribute('data-quantity-change'), 10);
    var min = parseInt(input.getAttribute('min') || '0', 10);
    var next = (parseInt(input.value, 10) || 0) + delta;
    if (next < min) next = min;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ----------------------------------------------------------------- cart */

  var Cart = {
    /**
     * Re-renders the drawer and every cart count from the section rendering
     * API, so markup stays owned by Liquid rather than duplicated in JS.
     */
    refreshSections: function (sectionsPayload) {
      if (!sectionsPayload) return;

      Object.keys(sectionsPayload).forEach(function (id) {
        var html = sectionsPayload[id];
        if (!html) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');

        document.querySelectorAll('[data-cart-section="' + id + '"]').forEach(function (mount) {
          var replacement = parsed.querySelector('[data-cart-section="' + id + '"]');
          if (replacement) {
            mount.innerHTML = replacement.innerHTML;
            initAccordion(mount);
          }
        });
      });
    },

    updateCounts: function (cart) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
      });
    },

    sectionsToRequest: function () {
      var ids = [];
      document.querySelectorAll('[data-cart-section]').forEach(function (el) {
        var id = el.getAttribute('data-cart-section');
        if (ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    },

    add: function (formData, submitButton) {
      var sections = Cart.sectionsToRequest();
      if (sections.length) formData.append('sections', sections.join(','));

      if (submitButton) submitButton.setAttribute('aria-disabled', 'true');

      return fetchJSON(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (payload) {
          Cart.refreshSections(payload.sections);
          return fetchJSON(routes.cart_url + '.js');
        })
        .then(function (cart) {
          Cart.updateCounts(cart);
          announce(strings.addToCart + ' — ' + cart.item_count);

          if (window.cartDrawerEnabled) {
            Overlay.open(document.querySelector('.cart-drawer'), submitButton);
          } else {
            window.location = routes.cart_url;
          }
        })
        .finally(function () {
          if (submitButton) submitButton.removeAttribute('aria-disabled');
        });
    },

    change: function (line, quantity, scope) {
      var sections = Cart.sectionsToRequest();
      if (scope) scope.classList.add('loading');

      return fetchJSON(routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: sections.join(',') })
      })
        .then(function (cart) {
          Cart.refreshSections(cart.sections);
          Cart.updateCounts(cart);

          // The full cart page has no drawer to re-render; reload its section.
          if (document.body.classList.contains('template-cart') && !cart.sections) {
            window.location.reload();
          }
        })
        .finally(function () {
          if (scope) scope.classList.remove('loading');
        });
    }
  };

  // Add to cart
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('form[data-product-form]');
    if (!form) return;

    event.preventDefault();

    var button = form.querySelector('[type="submit"]');
    var formData = new FormData(form);

    Cart.add(formData, button).catch(function (error) {
      var errorEl = form.querySelector('[data-form-error]');
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
      announce(error.message);
    });
  });

  // Quantity change + remove inside cart / drawer
  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-cart-quantity]');
    if (!input) return;

    var line = input.getAttribute('data-line');
    Cart.change(line, input.value, input.closest('.cart-item'));
  });

  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;

    event.preventDefault();
    Cart.change(remove.getAttribute('data-line'), 0, remove.closest('.cart-item'));
  });

  /* ------------------------------------------------------- variant picker */

  /**
   * Reads the product's variant JSON, matches the selected options, then
   * updates price, availability, media and the ?variant= URL param.
   */
  function initVariantPicker(root) {
    var dataEl = root.querySelector('[data-variant-json]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    // Products with only a default variant render no option fieldsets, so
    // there is nothing to match on. Liquid has already rendered the correct
    // button state and variant id — bail out and leave it alone. Without this
    // the matcher below resolves nothing and disables a buyable product.
    if (root.querySelectorAll('.variant-picker fieldset').length === 0) return;

    var form = root.querySelector('form[data-product-form]');
    var idInput = form && form.querySelector('input[name="id"]');
    var priceMount = root.querySelector('[data-price-mount]');
    var submit = form && form.querySelector('[type="submit"]');
    var stickySubmit = document.querySelector('[data-sticky-submit]');

    function currentOptions() {
      return Array.prototype.map.call(
        root.querySelectorAll('.variant-picker fieldset'),
        function (fieldset) {
          var checked = fieldset.querySelector('input:checked');
          return checked ? checked.value : null;
        }
      );
    }

    function findVariant(options) {
      // Only match when we actually read a value for every option. A partial
      // read must never resolve to "no variant" and disable the button.
      if (!options.length) return variants[0];

      return variants.find(function (variant) {
        return variant.options.every(function (option, index) {
          return option === options[index];
        });
      });
    }

    function markUnavailable(options) {
      root.querySelectorAll('.variant-picker fieldset').forEach(function (fieldset, fieldsetIndex) {
        fieldset.querySelectorAll('input').forEach(function (input) {
          var probe = options.slice();
          probe[fieldsetIndex] = input.value;

          var match = variants.find(function (variant) {
            return variant.options.every(function (option, i) {
              return probe[i] == null || option === probe[i];
            });
          });

          var label = fieldset.querySelector('label[for="' + input.id + '"]');
          if (label) label.setAttribute('data-unavailable', String(!match || !match.available));
        });
      });
    }

    function render() {
      var options = currentOptions();
      var variant = findVariant(options);

      markUnavailable(options);

      if (!variant) {
        if (submit) {
          submit.setAttribute('aria-disabled', 'true');
          submit.textContent = strings.unavailable;
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (submit) {
        if (variant.available) {
          submit.removeAttribute('aria-disabled');
          submit.textContent = strings.addToCart;
        } else {
          submit.setAttribute('aria-disabled', 'true');
          submit.textContent = strings.soldOut;
        }
      }

      if (stickySubmit) {
        stickySubmit.textContent = variant.available ? strings.addToCart : strings.soldOut;
        stickySubmit.toggleAttribute('aria-disabled', !variant.available);
      }

      if (priceMount) {
        var html = '<span class="price__current">' + formatMoney(variant.price) + '</span>';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html += '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
        }
        priceMount.innerHTML = html;
        priceMount.classList.toggle(
          'price--on-sale',
          Boolean(variant.compare_at_price && variant.compare_at_price > variant.price)
        );
      }

      if (variant.featured_media) {
        selectMedia(root, String(variant.featured_media.id));
      }

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    root.addEventListener('change', function (event) {
      if (event.target.closest('.variant-picker')) render();
    });

    render();
  }

  /* --------------------------------------------------------- product media */

  function selectMedia(root, mediaId) {
    var main = root.querySelector('[data-media-main]');
    var thumb = root.querySelector('[data-media-thumb][data-media-id="' + mediaId + '"]');
    if (!main || !thumb) return;

    var img = thumb.querySelector('img');
    var mainImg = main.querySelector('img');
    if (img && mainImg) {
      mainImg.src = img.getAttribute('data-full') || img.src;
      mainImg.srcset = '';
      mainImg.alt = img.alt;
    }

    root.querySelectorAll('[data-media-thumb]').forEach(function (el) {
      el.setAttribute('aria-current', String(el === thumb));
    });
  }

  document.addEventListener('click', function (event) {
    var thumb = event.target.closest('[data-media-thumb]');
    if (!thumb) return;

    event.preventDefault();
    var root = thumb.closest('[data-product-root]');
    if (root) selectMedia(root, thumb.getAttribute('data-media-id'));
  });

  /* ------------------------------------------------------------ sticky ATC */

  function initStickyAtc() {
    var sticky = document.querySelector('.product__sticky-atc');
    var form = document.querySelector('form[data-product-form]');
    if (!sticky || !form) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          sticky.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px -80px 0px' }
    );

    observer.observe(form);

    var stickyButton = sticky.querySelector('[data-sticky-submit]');
    if (stickyButton) {
      stickyButton.addEventListener('click', function () {
        if (stickyButton.hasAttribute('aria-disabled')) return;
        form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }
  }

  /* ---------------------------------------------------- predictive search */

  function initPredictiveSearch() {
    var modal = document.querySelector('.search-modal');
    if (!modal) return;

    var input = modal.querySelector('[data-search-input]');
    var results = modal.querySelector('[data-search-results]');
    if (!input || !results) return;

    var timer;
    var controller;

    input.addEventListener('input', function () {
      clearTimeout(timer);
      var term = input.value.trim();

      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }

      timer = setTimeout(function () {
        if (controller) controller.abort();
        controller = new AbortController();

        var url = routes.predictive_search_url +
          '?q=' + encodeURIComponent(term) +
          '&resources[type]=product,page,article' +
          '&resources[limit]=6' +
          '&section_id=predictive-search';

        fetch(url, { signal: controller.signal })
          .then(function (response) { return response.text(); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var inner = parsed.querySelector('[data-search-results-inner]');
            results.innerHTML = inner ? inner.innerHTML : '';
          })
          .catch(function (error) {
            if (error.name !== 'AbortError') results.innerHTML = '';
          });
      }, 250);
    });
  }

  /* ------------------------------------------------------- collection sort */

  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-sort-select]');
    if (!select) return;

    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location = url.toString();
  });

  // Facet checkboxes submit their form automatically.
  document.addEventListener('change', function (event) {
    var facet = event.target.closest('[data-facet-input]');
    if (!facet) return;

    var form = facet.closest('form');
    if (form) form.submit();
  });

  /* ------------------------------------------------------------------ init */

  function init(scope) {
    var root = scope || document;
    initAccordion(root);

    root.querySelectorAll('[data-product-root]').forEach(initVariantPicker);

    initStickyAtc();
    initPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor lifecycle — re-init sections as merchants edit them. */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:select', function (event) { init(event.target); });
})();
